import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ChatIcon from '@mui/icons-material/Chat'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import LogoutIcon from '@mui/icons-material/Logout'
import HelpIcon from '@mui/icons-material/Help'
import LanguageIcon from '@mui/icons-material/Language'
import { useAuth } from '@/contexts/AuthContext'
import { useSocketContext } from '@/context/SocketContext'
import { formatBadgeCount } from '@/context/useTabAttention'
import { ROUTES } from '@/constants'
import AppLogo from './AppLogo'
import SubmenuPopper from './SubmenuPopper'
import LanguageModal from './LanguageModal'
import {
  SidebarContainer,
  NavigationMenu,
  NavItem,
  NavBadge,
  ItemText,
  SubmenuContainer,
  SubmenuItem,
} from './styles'

type MenuItem = {
  label: string
  icon?: React.ElementType
  onClick?: () => void
  children?: MenuItem[]
}

type SidebarProps = {
  expanded: boolean
  onExpandedChange: (expanded: boolean) => void
}

export default function Sidebar({ expanded, onExpandedChange }: Readonly<SidebarProps>) {
  const [anchorEls, setAnchorEls] = useState<HTMLElement[]>([])
  const [openMenus, setOpenMenus] = useState<MenuItem[][]>([])
  const [showLanguageModal, setShowLanguageModal] = useState(false)

  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { t } = useTranslation()
  const { unreadIds } = useSocketContext()

  const navItems = [
    { route: ROUTES.FLOWS,         icon: AccountTreeIcon, label: t('sidebar.flow'),          badge: 0              },
    { route: ROUTES.CONVERSATIONS, icon: ChatIcon,         label: t('sidebar.conversations'), badge: unreadIds.size },
  ]

  const lowerMenuItems: MenuItem[] = [
    {
      label: t('sidebar.help'),
      icon: HelpIcon,
      children: [
        { label: t('sidebar.version'), icon: HelpIcon },
      ],
    },
    {
      label: user?.name ?? '',
      icon: AccountCircleIcon,
      children: [
        {
          label: t('common.sign_out'),
          icon: LogoutIcon,
          onClick: logout,
        },
      ],
    },
  ]

  const handleItemClick = (event: React.MouseEvent<HTMLElement>, level: number, item: MenuItem) => {
    event.stopPropagation()

    const isSameItem =
      level === 0
        ? anchorEls[level] === event.currentTarget
        : anchorEls[level] === event.currentTarget && openMenus[level] === item.children

    if (isSameItem) {
      setAnchorEls((prev) => prev.slice(0, level))
      setOpenMenus((prev) => prev.slice(0, level))
    } else {
      const newAnchorEls = [...anchorEls]
      const newOpenMenus = [...openMenus]
      newAnchorEls[level] = event.currentTarget
      newOpenMenus[level] = item.children ?? []
      setAnchorEls(newAnchorEls.slice(0, level + 1))
      setOpenMenus(newOpenMenus.slice(0, level + 1))
    }
  }

  const handleClose = () => {
    setAnchorEls([])
    setOpenMenus([])
  }

  const renderSubMenu = (items: MenuItem[]) => (
    <SubmenuContainer>
      {items.map((item, idx) => (
        <SubmenuItem
          key={idx}
          clickable={!!item.onClick}
          onClick={() => {
            item.onClick?.()
            handleClose()
          }}
        >
          {item.icon && <item.icon />}
          <span>{item.label}</span>
        </SubmenuItem>
      ))}
    </SubmenuContainer>
  )

  return (
    <SidebarContainer expanded={expanded}>
      <AppLogo expanded={expanded} onClick={() => onExpandedChange(!expanded)} />

      <NavigationMenu>
        <div>
          {navItems.map((item) => (
            <NavItem
              key={item.route}
              selected={location.pathname.startsWith(item.route)}
              expanded={expanded}
              onClick={() => navigate(item.route)}
              title={item.badge > 0 ? t('attention.banner', { count: item.badge }) : undefined}
            >
              <item.icon style={{ fontSize: 22, flexShrink: 0 }} />
              {item.badge > 0 && (
                <NavBadge key={item.badge} aria-label={t('attention.banner', { count: item.badge })}>
                  {formatBadgeCount(item.badge)}
                </NavBadge>
              )}
              <ItemText expanded={expanded}>{item.label}</ItemText>
            </NavItem>
          ))}
        </div>

        <div>
          {/* Language picker button */}
          <NavItem
            selected={false}
            expanded={expanded}
            onClick={() => setShowLanguageModal(true)}
            title={t('language.select')}
          >
            <LanguageIcon style={{ fontSize: 22, flexShrink: 0 }} />
            <ItemText expanded={expanded}>{t('language.modal_title')}</ItemText>
          </NavItem>

          {lowerMenuItems.map((item, idx) => (
            <NavItem
              key={idx}
              withBorderTop={idx === 0}
              expanded={expanded}
              onClick={(e) =>
                item.children ? handleItemClick(e as React.MouseEvent<HTMLElement>, 0, item) : handleClose()
              }
            >
              {item.icon && <item.icon style={{ fontSize: 22, flexShrink: 0 }} />}
              <ItemText expanded={expanded} lineBreak>
                {item.label}
              </ItemText>
            </NavItem>
          ))}
        </div>
      </NavigationMenu>

      {openMenus.map(
        (subMenu, idx) =>
          anchorEls[idx] && (
            <SubmenuPopper
              key={idx}
              open={Boolean(anchorEls[idx])}
              anchorEl={anchorEls[idx]}
              onClickAway={(e) => {
                const target = e.target as HTMLElement
                if (target.closest('[data-menu-item]')) return
                handleClose()
              }}
            >
              <div data-menu-item>{renderSubMenu(subMenu)}</div>
            </SubmenuPopper>
          ),
      )}

      {showLanguageModal && (
        <LanguageModal onClose={() => setShowLanguageModal(false)} />
      )}
    </SidebarContainer>
  )
}
